import { getLocale, t, useI18n } from '../../i18n';
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import './JoinCodesPage.css';

interface JoinCode {
  id: string;
  code: string;
  is_active: boolean;
  expires_at: string | null;
  used_count: number;
  created_at: string | null;
}

interface Props {
  onBack: () => void;
}

const formatExpiry = (expiresAt: string | null | undefined): string => {
  if (!expiresAt) return '';
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return t('Expired');
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 0) return t('Expires in {hours}h {minutes}m', { hours: h, minutes: m });
  return t('Expires in {minutes}m', { minutes: m });
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString(getLocale());
};

export default function JoinCodesPage({ onBack }: Props) {
  useI18n();
  const [codes, setCodes] = useState<JoinCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState<Record<string, boolean>>({});

  const loadCodes = useCallback(async () => {
    const { data, error: err } = await supabase.rpc('get_company_join_codes');
    if (err) { setError(err.message); return; }
    const result = data as { success: boolean; error?: string; codes?: JoinCode[] };
    if (!result.success) { setError(result.error ?? t('Unknown error')); return; }
    setCodes(result.codes ?? []);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    loadCodes().finally(() => setIsLoading(false));
  }, [loadCodes]);

  const handleGenerate = async () => {
    setError('');
    setIsGenerating(true);
    try {
      const { data, error: err } = await supabase.rpc('generate_company_join_code');
      if (err) throw err;
      const result = data as { success: boolean; error?: string };
      if (!result.success) throw new Error(result.error);
      await loadCodes();
    } catch (err) {
      setError((err as Error).message || t('Failed to generate code'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = (code: string, id: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleDeactivate = async (codeId: string) => {
    setError('');
    setDeactivating((prev) => ({ ...prev, [codeId]: true }));
    try {
      const { data, error: err } = await supabase.rpc('deactivate_join_code', { p_code_id: codeId });
      if (err) throw err;
      const result = data as { success: boolean; error?: string };
      if (!result.success) throw new Error(result.error);
      await loadCodes();
    } catch (err) {
      setError((err as Error).message || t('Failed to deactivate code'));
    } finally {
      setDeactivating((prev) => ({ ...prev, [codeId]: false }));
    }
  };

  const activeCode = codes.find((c) => c.is_active && new Date(c.expires_at ?? 0) > new Date());
  const pastCodes = codes.filter((c) => !c.is_active || new Date(c.expires_at ?? 0) <= new Date());

  if (isLoading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className="page--full">
      <div className="join-codes__header">
        <button className="btn btn--secondary join-codes__back-btn" onClick={onBack}>
          {t('Back')}
        </button>
        <h1 className="page__title">{t('Join Codes')}</h1>
      </div>

      <p className="text-muted join-codes__intro">
        Generate a 6-character code and share it directly with a guard. They enter it in the
        Guardian app to join your company. Codes expire after 24 hours.
      </p>

      {error && <div className="msg--error">{error}</div>}

      {/* Active code */}
      {activeCode ? (
        <div className="join-codes__active-card">
          <div className="join-codes__active-label">{t('Active Join Code')}</div>
          <div className="join-codes__code-display">{activeCode.code}</div>
          <div className="join-codes__expiry">{formatExpiry(activeCode.expires_at)}</div>
          {activeCode.used_count > 0 && (
            <div className="text-muted join-codes__used">
              Used {activeCode.used_count} time{activeCode.used_count !== 1 ? 's' : ''}
            </div>
          )}
          <div className="join-codes__active-actions">
            <button
              className="btn btn--primary"
              onClick={() => handleCopy(activeCode.code, activeCode.id)}
            >
              {copiedId === activeCode.id ? 'Copied!' : 'Copy Code'}
            </button>
            <button
              className="btn btn--secondary"
              onClick={() => handleDeactivate(activeCode.id)}
              disabled={deactivating[activeCode.id]}
            >
              {deactivating[activeCode.id] ? 'Deactivating...' : 'Deactivate'}
            </button>
            <button
              className="btn btn--secondary"
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate New'}
            </button>
          </div>
        </div>
      ) : (
        <div className="join-codes__empty-state">
          <p className="text-muted">{t('No active join code. Generate one to let guards join your company.')}</p>
          <button
            className="btn btn--primary join-codes__generate-btn"
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate Join Code'}
          </button>
        </div>
      )}

      {/* Past codes */}
      {pastCodes.length > 0 && (
        <div className="join-codes__history">
          <h2 className="section__title">{t('Code History')}</h2>
          <table className="table">
            <thead>
              <tr>
                <th>{t('Code')}</th>
                <th>{t('Created')}</th>
                <th>{t('Expired / Deactivated')}</th>
                <th>{t('Uses')}</th>
              </tr>
            </thead>
            <tbody>
              {pastCodes.map((c) => (
                <tr key={c.id}>
                  <td className="join-codes__history-code">{c.code}</td>
                  <td className="text-muted">{formatDate(c.created_at)}</td>
                  <td className="text-muted">
                    {!c.is_active ? 'Deactivated' : formatDate(c.expires_at)}
                  </td>
                  <td className="text-muted">{c.used_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
